import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  doc,
  runTransaction,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, from, map, catchError, of, switchMap, forkJoin } from 'rxjs';
import { CollectionTables } from '../models/CollectionTables.model';
import { firestoreTimestampNow, prepareFirestoreWrite } from '../utils/firestore-timestamp.util';
import { AdminEmployee } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  /**
   * Resolve which top-level collection the business document lives in.
   * New businesses use `businesses`; legacy data uses `administrators`.
   */
  private async resolveBusinessCollection(businessId: string): Promise<string> {
    try {
      const snap = await getDoc(doc(this.firestore, CollectionTables.businesses, businessId));
      return snap.exists() ? CollectionTables.businesses : CollectionTables.administrators;
    } catch {
      return CollectionTables.administrators;
    }
  }

  /**
   * Get all admin employees for a specific business
   */
  getAdminsByAdministratorId(businessId: string): Observable<AdminEmployee[]> {
    return from(this.resolveBusinessCollection(businessId)).pipe(
      switchMap((collectionName) => {
        const employeesRef = collection(
          this.firestore,
          collectionName,
          businessId,
          CollectionTables.employees
        );
        const q = query(employeesRef, where('role', '==', 'admin'));
        return from(getDocs(q)).pipe(
          map((snapshot) =>
            snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as AdminEmployee))
          ),
          catchError((error) => {
            console.error('❌ Error fetching admins:', error);
            return of([]);
          })
        );
      })
    );
  }

  /**
   * Get ALL employees for a specific business (no role filter)
   */
  getAllEmployees(businessId: string): Observable<AdminEmployee[]> {
    return from(this.resolveBusinessCollection(businessId)).pipe(
      switchMap((collectionName) => {
        const employeesRef = collection(
          this.firestore,
          collectionName,
          businessId,
          CollectionTables.employees
        );
        return from(getDocs(query(employeesRef))).pipe(
          map((snapshot) =>
            snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as AdminEmployee))
          ),
          catchError((error) => {
            console.error('Error fetching employees:', error);
            return of([]);
          })
        );
      })
    );
  }

  /**
   * Get multiple employees by ids
   */
  getEmployeesByIds(
    businessId: string,
    employeeIds: string[]
  ): Observable<AdminEmployee[]> {
    if (employeeIds.length === 0) return of([]);
    const uniq = [...new Set(employeeIds)];
    return forkJoin(uniq.map((id) => this.getEmployeeById(businessId, id))).pipe(
      map((list) => list.filter((e): e is AdminEmployee => e != null)),
      catchError((err) => {
        console.error('Error fetching employees by ids:', err);
        return of([]);
      })
    );
  }

  /**
   * Get a single employee by id
   */
  getEmployeeById(
    businessId: string,
    employeeId: string
  ): Observable<AdminEmployee | null> {
    return from(this.resolveBusinessCollection(businessId)).pipe(
      switchMap((collectionName) => {
        const empRef = doc(
          this.firestore,
          collectionName,
          businessId,
          CollectionTables.employees,
          employeeId
        );
        return from(getDoc(empRef)).pipe(
          map((snap) => {
            if (!snap.exists()) return null;
            return { id: snap.id, ...snap.data() } as AdminEmployee;
          }),
          catchError((err) => {
            console.error('Error fetching employee:', err);
            return of(null);
          })
        );
      })
    );
  }

  /**
   * Add a new employee (manager-only). Uses auto-generated doc id and stores it as `id`.
   */
  addEmployee(
    businessId: string,
    data: {
      name: string;
      email: string;
      phone?: string;
      role: string;
    }
  ): Observable<string> {
    return from(this.resolveBusinessCollection(businessId)).pipe(
      switchMap((collectionName) => {
        const employeesRef = collection(
          this.firestore,
          collectionName,
          businessId,
          CollectionTables.employees
        );
        const empRef = doc(employeesRef);
        const now = firestoreTimestampNow();
        const payload = {
          id: empRef.id,
          name: data.name.trim(),
          email: (data.email || '').trim().toLowerCase(),
          phone: (data.phone || '').trim() || null,
          role: data.role || 'clerk',
          businessId,
          businessType: 'management',
          userId: null, // Set when user accepts invitation
          createdOn: now,
          version: 1,
        };
        return from(
          setDoc(empRef, prepareFirestoreWrite(payload) as Record<string, unknown>)
        ).pipe(
          map(() => empRef.id),
          catchError((err) => {
            console.error('Error adding employee:', err);
            throw err;
          })
        );
      })
    );
  }

  /**
   * Update an existing employee
   */
  updateEmployee(
    businessId: string,
    employeeId: string,
    data: Partial<{
      name: string;
      email: string;
      phone: string;
      role: string;
      invitationSent: boolean;
    }>
  ): Observable<void> {
    return from(this.resolveBusinessCollection(businessId)).pipe(
      switchMap((collectionName) => {
        const empRef = doc(
          this.firestore,
          collectionName,
          businessId,
          CollectionTables.employees,
          employeeId
        );
        const payload: Record<string, unknown> = {
          // Ensure document id is always mirrored on the employee document.
          id: employeeId,
        };
        if (data.name !== undefined) payload['name'] = data.name.trim();
        if (data.email !== undefined) payload['email'] = data.email.trim().toLowerCase();
        if (data.phone !== undefined) payload['phone'] = data.phone.trim() || null;
        if (data.role !== undefined) payload['role'] = data.role;
        if (data.invitationSent !== undefined) {
          payload['invitationSent'] = !!data.invitationSent;
        }
        return from(updateDoc(empRef, payload));
      })
    );
  }

  /**
   * Remove an admin employee
   */
  removeAdmin(adminEmployee: AdminEmployee): Observable<boolean> {
    return this.deleteEmployee(
      adminEmployee.businessId || adminEmployee.administratorId || '',
      adminEmployee.id
    );
  }

  /**
   * Delete permission rows and the employee document atomically.
   */
  deleteEmployee(
    businessId: string,
    employeeId: string
  ): Observable<boolean> {
    return from(this.deleteEmployeeInTransaction(businessId, employeeId)).pipe(
      catchError((err) => {
        console.error('Error deleting employee:', err);
        return of(false);
      })
    );
  }

  private async deleteEmployeeInTransaction(
    businessId: string,
    employeeId: string
  ): Promise<boolean> {
    const collectionName = await this.resolveBusinessCollection(businessId);
    const permissionsRef = collection(this.firestore, CollectionTables.permissions);
    const permQuery = query(permissionsRef, where('employeeId', '==', employeeId));
    const permSnap = await getDocs(permQuery);
    const empRef = doc(
      this.firestore,
      collectionName,
      businessId,
      CollectionTables.employees,
      employeeId
    );
    await runTransaction(this.firestore, async (transaction) => {
      permSnap.docs.forEach((d) => transaction.delete(d.ref));
      transaction.delete(empRef);
    });
    return true;
  }
}
